(() => {
  if (window.EmbryniumAnalytics) return;

  const prefix = 'embrynium:';
  const emitted = [];
  let previousDraftCount = 0;
  const reachedTiers = new Set();

  const analyticsAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  };

  const cleanData = (data = {}) => {
    const allowedKeys = new Set([
      'available',
      'builder_item_count',
      'currency',
      'destination_path',
      'eligible_item_count',
      'product_id',
      'source',
      'tier',
      'value',
      'variant_id',
    ]);
    return Object.fromEntries(
      Object.entries(data).filter(([key, value]) => allowedKeys.has(key) && ['string', 'number', 'boolean'].includes(typeof value))
    );
  };

  const publish = (name, data = {}) => {
    if (!/^[a-z][a-z0-9_]*$/.test(name) || !analyticsAllowed()) return false;
    const payload = cleanData(data);
    emitted.push({ name, data: payload });
    if (emitted.length > 50) emitted.shift();

    document.dispatchEvent(new CustomEvent('embrynium:analytics', {
      detail: { name, data: payload },
    }));

    try {
      const result = window.Shopify?.analytics?.publish?.(`${prefix}${name}`, payload);
      result?.catch?.(() => undefined);
    } catch {
      return false;
    }
    return true;
  };

  document.addEventListener('embrynium:variant:selected', (event) => {
    publish('variant_selected', {
      variant_id: String(event.detail?.variantId || ''),
      available: Boolean(event.detail?.available),
      source: 'product_page',
    });
  });

  document.addEventListener('embrynium:bundle:draft-updated', (event) => {
    const draft = event.detail?.draft || [];
    const count = draft.length;
    const source = event.detail?.source || 'interface';

    if (source !== 'cart' && previousDraftCount === 0 && count > 0) {
      publish('bundle_started', { builder_item_count: count, source });
    }
    if (source !== 'cart' && count > previousDraftCount) {
      publish('bundle_item_selected', {
        builder_item_count: count,
        variant_id: String(draft[count - 1]?.variantId || ''),
        source,
      });
      publish('add_to_bundle', {
        builder_item_count: count,
        variant_id: String(draft[count - 1]?.variantId || ''),
        source,
      });
    }

    const tier = count >= 3 ? 3 : count >= 2 ? 2 : 0;
    if (source !== 'cart' && tier && !reachedTiers.has(tier)) {
      reachedTiers.add(tier);
      publish('bundle_tier_reached', { tier, builder_item_count: count, source });
    }
    previousDraftCount = count;
  });

  document.addEventListener('embrynium:bundle:completed', (event) => {
    const draft = event.detail?.draft || [];
    publish('bundle_added_to_cart', {
      builder_item_count: draft.length,
      eligible_item_count: event.detail?.cart?.item_count || draft.length,
      tier: draft.length >= 3 ? 3 : draft.length >= 2 ? 2 : 0,
      source: 'bundle_builder',
    });
  });

  document.addEventListener('embrynium:cart:updated', (event) => {
    if (event.detail?.source !== 'embrynium-quick-shop') return;
    publish('add_to_cart', {
      variant_id: String(event.detail?.variantId || ''),
      source: 'quick_shop',
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action || window.location.href, window.location.origin);
    const searchPath = window.routes?.search_url || `${window.Shopify?.routes?.root || '/'}search`;
    if (action.pathname === new URL(searchPath, window.location.origin).pathname) {
      publish('search_submitted', { source: 'storefront_search' });
    }
  });

  document.addEventListener('focusin', (event) => {
    const form = event.target.closest?.('[data-emb-story-form]');
    if (!form || form.dataset.analyticsStarted === 'true') return;
    form.dataset.analyticsStarted = 'true';
    publish('story_submission_started', { source: 'community_story_form' });
  });

  document.addEventListener('play', (event) => {
    if (!event.target.closest?.('[data-emb-featured-story]')) return;
    publish('featured_story_played', { source: 'community_story_gallery' });
  }, true);

  if (document.querySelector('[data-emb-story-success]')) {
    publish('story_submission_completed', { source: 'community_story_form' });
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const destination = new URL(link.href, window.location.origin);

    if (link.closest('[data-emb-search-results], #predictive-search-results')) {
      publish('search_result_selected', {
        destination_path: destination.pathname,
        source: link.closest('#predictive-search-results') ? 'predictive_search' : 'search_results',
      });
    }
    if (link.closest('.emb-archive-chapter')) {
      publish('archive_explored', {
        destination_path: destination.pathname,
        source: 'archive_chapter',
      });
    }
    if (link.matches('[href*="size-guide"], [data-size-guide]')) {
      publish('size_guide_opened', { destination_path: destination.pathname, source: 'storefront' });
    }
    if (link.matches('[href*="#archive-reviews"], [data-review-link]')) {
      publish('review_viewed', { destination_path: destination.pathname, source: 'storefront' });
    }
    if (link.matches('[href="#embrynium-stories"]')) {
      publish('story_cta_clicked', { destination_path: '#embrynium-stories', source: 'storefront' });
    }
  });

  window.EmbryniumAnalytics = Object.freeze({
    publish,
    analyticsAllowed,
    getDebugEvents: () => emitted.map((event) => ({ ...event, data: { ...event.data } })),
  });
})();
